/**
 * Date-of-birth arithmetic that stays in the APP rather than the component library.
 *
 * The masking and ISO conversion used to live here, alongside a hidden
 * `<input type="date">` driven from a button. Both moved into `@fluxora/ui`'s
 * `DatePicker` when the native picker was replaced — a native control cannot be told
 * which format to display, nor where to open its popup, and steering the latter by
 * sizing a hidden anchor never held. Use `maskDateInput`, `dateDisplayToIso` and
 * `isoToDateDisplay` from `@fluxora/ui` for that work now.
 *
 * What remains here is the one thing that is NOT the picker's business: WHICH DATES
 * ARE ELIGIBLE. `DatePicker`'s own header says it in as many words — "no business
 * rules … a caller that relies on this component to enforce '18 or older' has put a
 * domain rule in the component library". So the arithmetic lives here, the RULE that
 * consumes it lives in `schemas/dob.schema.ts`, and the picker only receives the
 * resulting `max` string.
 *
 * ⚠️ ONE ARBITER FOR "IS THIS PERSON 18?". `adultDobCutoffIso` (an ISO bound, used by
 * the picker AND the schema) and `ageInYears` (whole years, used by the §25.478
 * age-waiver offer) must never disagree. `__tests__/dob-format.test.ts` pins that
 * agreement day-by-day across leap boundaries rather than trusting two derivations.
 */

/**
 * The Texas enrollment minimum. A retail electric contract is a contract; a minor
 * cannot be held to one, so this is an eligibility rule and not a nicety.
 *
 * ⚠️ NEITHER `@rheora/enrollment-contract` NOR US-Enrollment-BFF ENFORCES AN AGE
 * (verified 2026-08-04: `CreditIdentitySchema.dateOfBirth` is a bare
 * `/^\d{4}-\d{2}-\d{2}$/u` and its comment says "bounded to a plausible adult DOB
 * range is enforced FE-side"; `routes/credit.routes.ts` forwards the DOB untouched).
 * So this constant is currently the ONLY place the rule exists in this repo. It is a
 * client-side rule, which means it is a courtesy and not a control — a caller that
 * never runs our JavaScript is not stopped by it. Proposing the same bound on the
 * contract schema is a cross-repo change and is filed, not made here.
 */
export const MINIMUM_ENROLLMENT_AGE_YEARS = 18;

/**
 * The oldest age we will accept before treating the value as a typo rather than a
 * person. Above this a mistyped year (1090 for 1990) is far likelier than a
 * supercentenarian, and telling that customer "you must be at least 18" — which is
 * what happened before this had its own bound — is nonsense.
 */
export const MAXIMUM_PLAUSIBLE_AGE_YEARS = 120;

/**
 * `now` as `YYYY-MM-DD`.
 *
 * ⚠️ Local date parts, not `toISOString()`. The latter rolls the date forward in any
 * timezone behind UTC, so a customer opening the form at 23:30 in America/Chicago
 * would see tomorrow's date.
 */
export function todayIso(now: Date): string {
  return `${String(now.getFullYear()).padStart(4, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * The MOST RECENT date of birth that is already 18 as of `now` — i.e. the calendar's
 * `max`, and the same bound the Zod schema enforces.
 *
 * Someone whose 18th birthday is TODAY is eligible, so the cutoff is inclusive: it is
 * exactly their date of birth, and `DatePicker` disables a day only when `iso > max`.
 * Someone whose 18th birthday is tomorrow sits one day past it and is greyed out.
 *
 * ── LEAP DAY: CLAMPED DOWN TO FEB 28, NEVER SHIFTED A YEAR ──────────────────
 * On 29 February the naive `new Date(y - 18, m, d)` produces 1 March of the cutoff
 * year, because JS Date silently rolls a non-existent date forward — which would make
 * someone born on 1 March eligible a day EARLY. Two failure modes are possible here
 * and both are rejected: throwing (a customer cannot open a calendar one day in four
 * years) and rolling forward (a 17-year-old passes). So the cutoff is built as a
 * STRING and, when the resulting 29 February does not exist, clamped BACK to the
 * 28th. That is the conservative direction: on 2024-02-29 the latest eligible DOB is
 * 2006-02-28 (that person turned 18 on 2006→2024-02-28), and 2006-03-01 stays
 * ineligible for one more day.
 *
 * The mirror case — a customer BORN on 29 February — is handled by `ageInYears`
 * below, which treats their birthday as 1 March in a non-leap year (they are 17 on
 * 2026-02-28 and 18 on 2026-03-01). Both functions agree on every such day; the test
 * file walks them.
 */
export function adultDobCutoffIso(now: Date): string {
  const today = todayIso(now);
  const cutoffYear = String(
    Number(today.slice(0, 4)) - MINIMUM_ENROLLMENT_AGE_YEARS,
  ).padStart(4, '0');
  const candidate = `${cutoffYear}-${today.slice(5)}`;
  // The month/day pair came from a real date, so the ONLY way this can fail to exist
  // is 29 February landing in a non-leap cutoff year — hence the literal 02-28.
  //
  // ⚠️ IT IS DETECTED BY A ROUND TRIP, NOT BY `Number.isNaN`. `new Date('2006-02-29')`
  // does NOT produce an invalid date: the ISO grammar accepts any DD from 01 to 31 and
  // MakeDay rolls the overflow forward, so it silently becomes 1 March 2006 — which
  // would hand an extra day of eligibility to everyone born on 1 March. Comparing the
  // parsed date back against the string is what actually proves the day exists. (The
  // same reason `@fluxora/ui`'s `dateDisplayToIso` round-trips.) The NaN guard stays
  // in front of `toISOString`, which throws on an invalid date, so this can never
  // fail to return a calendar bound.
  const parsed = new Date(`${candidate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return `${cutoffYear}-02-28`;
  return parsed.toISOString().slice(0, 10) === candidate ? candidate : `${cutoffYear}-02-28`;
}

/**
 * Whole years between `dobIso` (YYYY-MM-DD) and `asOf`. `NaN` for an unparseable
 * date — callers treat that as "unknown", never as zero.
 *
 * Used for the §25.478(e) age-waiver OFFER as well as for the eligibility bound,
 * which is why it stays a number rather than a boolean.
 *
 * ── CALENDAR ARITHMETIC ON STRINGS, NOT ON `Date` ──────────────────────────
 * `asOf` is reduced to its LOCAL calendar date by `todayIso` and then compared
 * month-day against the DOB as text (`'07-19' < '07-20'` is chronological for a
 * zero-padded MM-DD). Two reasons, both of which were real bugs waiting:
 *   1. The previous implementation read `asOf` in UTC while `adultDobCutoffIso` reads
 *      it locally, so for anyone west of Greenwich after 19:00 the picker's bound and
 *      the validator's answer described DIFFERENT days.
 *   2. Parsing the DOB into a `Date` and doing month/day maths on it is where the
 *      leap-day rollover creeps back in.
 * With both derived from `todayIso`, `ageInYears(d, asOf) >= 18` and
 * `d <= adultDobCutoffIso(asOf)` are equivalent for EVERY date that exists — the one
 * pair they could disagree on (a 29 February cutoff) is a date nobody can be born on,
 * because a leap year minus 18 is never itself a leap year.
 *
 * A 29-February BIRTHDAY is treated as 1 March in a non-leap year: born 2008-02-29,
 * you are 17 on 2026-02-28 and 18 on 2026-03-01. That is the conservative reading and
 * the one the cutoff agrees with; it never throws and never shifts by a whole year.
 */
export function ageInYears(dobIso: string, asOf: Date): number {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(dobIso)) return Number.NaN;
  const today = todayIso(asOf);
  const age = Number(today.slice(0, 4)) - Number(dobIso.slice(0, 4));
  // Birthday not yet reached this year → one fewer whole year.
  return today.slice(5) < dobIso.slice(5) ? age - 1 : age;
}
